// Nova Scotia Security Services — service catalogue with deep SEO content.
export const services = [
  {
    slug: "mining-security",
    number: "01",
    name: "Mining Security",
    shortName: "Mining Security",
    tagline: "Rugged, remote-site protection for Nova Scotia's extraction industry.",
    keywords: [
      "mining security services Nova Scotia",
      "mine site security guards Halifax",
      "quarry security Cape Breton",
      "gold mine security Nova Scotia",
      "gypsum mine security Windsor",
      "coal mine security Cape Breton",
      "aggregate quarry patrol Nova Scotia",
      "remote industrial security guards NS",
      "extraction site security Antigonish",
      "24/7 mine gate security Nova Scotia",
    ],
    hero: "https://images.unsplash.com/photo-1660748255246-80f24d317070?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwbWluaW5nJTIwbmlnaHR8ZW58MHx8fHwxNzg1MTA1NjcyfDA&ixlib=rb-4.1.0&q=85",
    intro: "Nova Scotia's mining sector — from Cape Breton coalfields to Windsor gypsum, from gold operations in Moose River to aggregate quarries across the province — demands a security partner that understands remote logistics, high-value equipment, MSHA-adjacent protocols, and the harsh Atlantic climate. We staff every mine gate, muster station, and haul road with vetted guards who know how to work alongside heavy equipment operators, blast crews, and Indigenous liaison officers.",
    body: [
      "Our mining security teams are trained in confined-space awareness, MSDS and WHMIS handling, radio discipline on shared channels, and emergency evacuation drills modelled on the Westray legacy and current Occupational Health and Safety Act requirements. Every guard assigned to a Nova Scotia mine site carries a Nova Scotia Security Guard licence, a valid Standard First Aid + CPR-C certificate, and site-specific PPE — steel-toed CSA boots, high-visibility Class 3 outerwear, hardhat, safety glasses, and hearing protection.",
      "Beyond gatehouse manning, we deliver mobile patrols across expansive tailings ponds, fuel farms, explosives magazines, core-sample storage, and processing plants. Copper theft, diesel siphoning, and unauthorised ATV or snowmobile incursions during shoulder seasons are the three most common losses we prevent — and every patrol is logged with GPS-verified checkpoints delivered to your operations manager via daily activity reports.",
      "For remote greenfield and exploration camps — common in Guysborough, Pictou, and Colchester counties — we can scale to full turnkey coverage: badge issuance, contractor onboarding, muster-list reconciliation, drone-perimeter observation, and after-hours lockdown. When your project moves from feasibility to production, our roster moves with you.",
    ],
    deliverables: [
      "24/7 manned gatehouse & access control",
      "MSHA-informed muster reconciliation",
      "Mobile perimeter patrols with GPS checkpoints",
      "Explosives magazine & fuel farm surveillance",
      "Contractor onboarding & badge issuance",
      "Drone & CCTV integration",
      "WHMIS-trained guards, CSA-certified PPE",
      "Emergency evacuation & incident command support",
    ],
  },
  {
    slug: "corporate-security",
    number: "02",
    name: "Corporate & Office Security",
    shortName: "Corporate",
    tagline: "Front-of-house presence for banks, offices, and headquarters across Halifax.",
    keywords: [
      "corporate security Halifax",
      "office building security Dartmouth",
      "commercial security guards Nova Scotia",
      "bank security guard services Halifax",
      "law firm security Nova Scotia",
      "insurance office security Bedford",
    ],
    hero: "https://images.unsplash.com/photo-1566245024852-04fbf7842ce9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxzZWN1cml0eSUyMGd1YXJkJTIwcGF0cm9sJTIwZm9nfGVufDB8fHx8MTc4NTEwNTY3Mnww&ixlib=rb-4.1.0&q=85",
    intro: "From the towers of Barrington Street to the campus buildings of Burnside Industrial Park, our corporate officers project calm authority. Uniformed, articulate, and trained in de-escalation, they are the first face your clients meet and the last line before your assets.",
    body: [
      "We deploy dedicated site managers for buildings above 100,000 sq ft and cross-trained relief officers for smaller commercial tenants. Every officer understands access-control systems (Kantech, Genetec, HID, LenelS2), visitor management platforms, and the discreet escalation ladders your legal and HR teams expect.",
      "Halifax's downtown core, the Bayers Lake business district, and the Burnside industrial park all present very different risk profiles — from tenant disputes and homelessness outreach in the urban core to after-hours warehouse coverage in the industrial parks. Our corporate program is tuned to each.",
      "We interface directly with property managers, building engineers, and cleaning contractors so that access, incidents, and after-hours events are handled without escalation to executives. Monthly analytics show incident trends, false-alarm reductions, and dwell-time metrics.",
    ],
    deliverables: [
      "Lobby & front-desk officers",
      "Access-control system operation",
      "Visitor management & badge issuance",
      "After-hours lockdown & alarm response",
      "Executive floor discretion protocols",
      "Monthly incident analytics",
    ],
  },
  {
    slug: "retail-loss-prevention",
    number: "03",
    name: "Retail & Loss Prevention",
    shortName: "Retail LP",
    tagline: "Uniformed and plain-clothes officers who protect margin without disrupting the customer experience.",
    keywords: [
      "retail security Halifax",
      "loss prevention Nova Scotia",
      "shoplifting prevention Dartmouth",
      "mall security guards Halifax",
      "store security officer Sydney",
      "grocery store security Nova Scotia",
    ],
    hero: "https://images.unsplash.com/photo-1566245024852-04fbf7842ce9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxzZWN1cml0eSUyMGd1YXJkJTIwcGF0cm9sJTIwZm9nfGVufDB8fHx8MTc4NTEwNTY3Mnww&ixlib=rb-4.1.0&q=85",
    intro: "Organised retail crime in Nova Scotia has grown across grocery, pharmacy, and big-box categories. Our uniformed deterrent teams and plain-clothes LP investigators combine to reduce shrink without turning your store into a fortress.",
    body: [
      "Our LP investigators are trained in Powers of Arrest under the Criminal Code of Canada, use-of-force continuum, evidence handling, and courtroom testimony. We work with the Halifax Regional Police, RCMP, and Cape Breton Regional Police on repeat-offender files and CCTV disclosure.",
      "Uniformed officers focus on visible deterrence at entrances, high-value aisles (spirits, infant formula, cosmetics, meat), and self-checkout. Plain-clothes LP focuses on trained observation, external theft patterns, and internal audits.",
      "We integrate with your CCTV system, exception-based reporting from your POS, and your shrink dashboards. Every intervention is documented with time-stamped video and written statements ready for Crown disclosure.",
    ],
    deliverables: [
      "Uniformed store officers",
      "Plain-clothes LP investigators",
      "CCTV & POS exception review",
      "Internal audit & interview support",
      "Court-ready incident documentation",
    ],
  },
  {
    slug: "event-security",
    number: "04",
    name: "Event Security",
    shortName: "Events",
    tagline: "Concerts, festivals, weddings, and stadium crowds — controlled, calm, unforgettable.",
    keywords: [
      "event security Halifax",
      "concert security Nova Scotia",
      "festival security guards Halifax",
      "wedding security Nova Scotia",
      "stadium security Halifax",
      "conference security Dartmouth",
    ],
    hero: "https://images.unsplash.com/photo-1566245024852-04fbf7842ce9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxzZWN1cml0eSUyMGd1YXJkJTIwcGF0cm9sJTIwZm9nfGVufDB8fHx8MTc4NTEwNTY3Mnww&ixlib=rb-4.1.0&q=85",
    intro: "From the Halifax Jazz Festival to the Cabot Trail Relay, from private waterfront weddings to the Scotiabank Centre concert circuit — our event teams design and staff every touchpoint of your guest journey.",
    body: [
      "We provide licensed door supervisors, bag-check officers, credentialed backstage protection, and mobile response teams. Every event is preceded by a written security plan including ingress mapping, medical staging, weather contingency, and evacuation routes.",
      "Our supervisors are Halifax Regional Municipality Special Event Advisory Committee experienced — permits, noise bylaws, and liquor licence obligations are second nature.",
      "For festivals of over 5,000 attendees we coordinate directly with EHS Emergency Health Services, HRP, and provincial fire marshals to build an incident command structure worthy of your investment.",
    ],
    deliverables: [
      "Written event security plan",
      "Licensed door supervisors",
      "Credentialed backstage teams",
      "Ingress & egress crowd flow design",
      "Incident command coordination",
      "Post-event debrief report",
    ],
  },
  {
    slug: "mobile-patrol",
    number: "05",
    name: "Mobile Patrol",
    shortName: "Mobile Patrol",
    tagline: "Marked-vehicle patrols across HRM, CBRM, and the South Shore.",
    keywords: [
      "mobile patrol Halifax",
      "vehicle patrol security Nova Scotia",
      "alarm response Halifax",
      "lock up unlock Dartmouth",
      "mobile security guard Nova Scotia",
      "after hours patrol Cape Breton",
    ],
    hero: "https://images.unsplash.com/photo-1566245024852-04fbf7842ce9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxzZWN1cml0eSUyMGd1YXJkJTIwcGF0cm9sJTIwZm9nfGVufDB8fHx8MTc4NTEwNTY3Mnww&ixlib=rb-4.1.0&q=85",
    intro: "Marked-vehicle mobile patrol delivers scheduled, randomised, and reactive protection for sites that don't need dedicated guarding — but must never feel forgotten.",
    body: [
      "Our patrol supervisors run beat maps across Halifax Regional Municipality, Cape Breton Regional Municipality, and the South Shore corridor. Every visit is GPS-timestamped and photographed, with exception reporting sent within minutes if anything is out of place.",
      "We deliver lock-up/unlock services for retail and industrial tenants, alarm response with keyholder callouts, and welfare checks on lone workers.",
      "Every patrol vehicle carries a defibrillator, first-aid kit, incident camera, and a Nova Scotia-licensed guard trained in emergency response — because a patrol officer is often first on scene.",
    ],
    deliverables: [
      "Scheduled & randomised patrols",
      "Alarm response & keyholder callouts",
      "Lock-up / unlock services",
      "GPS-timestamped reporting",
      "Lone-worker welfare checks",
    ],
  },
  {
    slug: "construction-site-security",
    number: "06",
    name: "Construction Site Security",
    shortName: "Construction",
    tagline: "Copper, tools, and diesel — protected from mobilisation to substantial completion.",
    keywords: [
      "construction site security Halifax",
      "job site security Nova Scotia",
      "copper theft prevention Dartmouth",
      "tool theft security Halifax",
      "construction security guards Nova Scotia",
    ],
    hero: "https://images.unsplash.com/photo-1660748255246-80f24d317070?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwbWluaW5nJTIwbmlnaHR8ZW58MHx8fHwxNzg1MTA1NjcyfDA&ixlib=rb-4.1.0&q=85",
    intro: "From Halifax's harbourfront highrises to wind-farm builds in Colchester and Antigonish, we protect general contractors, mechanical trades, and developers from the losses that quietly erode margin.",
    body: [
      "Our construction officers are trained in fall-arrest awareness, WHMIS, and construction-safety orientation. They understand the difference between a legitimate 6:30 a.m. drywall crew and a stranger with a bolt cutter.",
      "Trailer break-ins, diesel siphoning, copper spool theft, and tool cage burglaries are stopped through a combination of on-site presence, mobile patrols, and temporary CCTV/floodlight towers we can deploy the same week.",
      "We work seamlessly with the general contractor's site super and interface with your Nova Scotia Construction Safety Association safety documentation.",
    ],
    deliverables: [
      "On-site overnight guarding",
      "Mobile patrols during mobilisation",
      "CCTV & floodlight tower deployment",
      "Trailer & tool cage protection",
      "NSCSA-informed reporting",
    ],
  },
  {
    slug: "residential-estate-security",
    number: "07",
    name: "Residential & Estate Security",
    shortName: "Residential",
    tagline: "Discreet protection for gated homes, private estates, and residential condominiums.",
    keywords: [
      "residential security Halifax",
      "estate security Nova Scotia",
      "condo security guards Halifax",
      "gated community security Nova Scotia",
      "home security services Dartmouth",
    ],
    hero: "https://images.unsplash.com/photo-1706555693525-fb90cbe18826?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxub3ZhJTIwc2NvdGlhJTIwY29hc3R8ZW58MHx8fHwxNzg1MTA1Njc4fDA&ixlib=rb-4.1.0&q=85",
    intro: "For private clients along the South Shore, in Chester, Hubbards, and the estate corridors of Bedford and St. Margaret's Bay, we deliver security that never announces itself.",
    body: [
      "Our residential officers are hand-picked for the assignment. They wear soft uniforms or business attire, understand household staff dynamics, and prioritise the privacy of your family above all else.",
      "For high-rise condominiums in downtown Halifax, we provide concierge-integrated officers who handle package receiving, guest verification, and after-hours access.",
      "For multi-acre estates, we combine standing officers with mobile patrols, CCTV monitoring, and integration with your alarm provider.",
    ],
    deliverables: [
      "Discreet in-residence officers",
      "Concierge-integrated condo security",
      "Estate perimeter patrols",
      "CCTV & alarm integration",
      "Household staff coordination",
    ],
  },
  {
    slug: "concierge-front-desk",
    number: "08",
    name: "Concierge & Front Desk",
    shortName: "Concierge",
    tagline: "Hospitality-grade officers who elevate the first impression of your building.",
    keywords: [
      "concierge security Halifax",
      "front desk security Nova Scotia",
      "condominium concierge Halifax",
      "luxury building security Halifax",
      "hotel front desk security Nova Scotia",
    ],
    hero: "https://images.unsplash.com/photo-1566245024852-04fbf7842ce9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxzZWN1cml0eSUyMGd1YXJkJTIwcGF0cm9sJTIwZm9nfGVufDB8fHx8MTc4NTEwNTY3Mnww&ixlib=rb-4.1.0&q=85",
    intro: "The concierge is the face of your property. We provide bilingual, articulate officers with hospitality training who make security feel like service.",
    body: [
      "Every concierge officer is briefed on your building's residents, protocols, delivery windows, and preferred communication channels.",
      "From luxury condos on the Halifax waterfront to boutique hotels in Lunenburg, our concierge program blends warmth with vigilance.",
      "Package logging, contractor access, guest verification, and quiet-hours enforcement are all handled with the discretion your community expects.",
    ],
    deliverables: [
      "Bilingual concierge officers",
      "Package & delivery management",
      "Guest verification protocols",
      "Contractor access coordination",
      "Quiet-hours enforcement",
    ],
  },
  {
    slug: "executive-protection",
    number: "09",
    name: "Executive Protection",
    shortName: "Executive",
    tagline: "Close-protection specialists for executives, dignitaries, and family offices.",
    keywords: [
      "executive protection Nova Scotia",
      "close protection Halifax",
      "bodyguard services Halifax",
      "VIP security Nova Scotia",
      "family office security Halifax",
    ],
    hero: "https://images.unsplash.com/photo-1566245024852-04fbf7842ce9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxzZWN1cml0eSUyMGd1YXJkJTIwcGF0cm9sJTIwZm9nfGVufDB8fHx8MTc4NTEwNTY3Mnww&ixlib=rb-4.1.0&q=85",
    intro: "Our executive protection specialists are former military and law-enforcement professionals with international CP training. Every assignment begins with a threat assessment and ends with a discreet, on-time departure.",
    body: [
      "Whether you are a visiting dignitary attending a conference in Halifax, a Fortune 500 executive touring your Nova Scotia operations, or a family-office principal on a private visit, we build your protection detail around your travel, not around ours.",
      "We coordinate with Halifax Stanfield International Airport ground handlers, secure ground transport providers, and your event or venue security teams. Advance work, route planning, hotel security surveys, and residential secure-zone establishment are all included.",
      "Every EP detail is led by a Nova Scotia-licensed supervisor with formal close-protection training and years of principal-facing experience.",
    ],
    deliverables: [
      "Threat & route assessment",
      "Advance work & hotel surveys",
      "Secure ground transport coordination",
      "Airport meet-and-greet",
      "Residential secure-zone setup",
    ],
  },
  {
    slug: "marine-port-security",
    number: "10",
    name: "Marine & Port Security",
    shortName: "Marine & Port",
    tagline: "ISPS-informed maritime and terminal protection for Halifax and beyond.",
    keywords: [
      "port security Halifax",
      "marine security Nova Scotia",
      "ISPS compliance Halifax",
      "cargo security Halifax port",
      "cruise ship security Sydney",
      "shipyard security Nova Scotia",
    ],
    hero: "https://images.unsplash.com/photo-1606185540834-d6e7483ee1a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxjb2FzdGFsJTIwcG9ydCUyMHNoaXB8ZW58MHx8fHwxNzg1MTA1NjcyfDA&ixlib=rb-4.1.0&q=85",
    intro: "The Port of Halifax is one of the deepest natural harbours in the world and a critical Atlantic gateway. Our marine and port program supports terminal operators, cruise lines, and shipyards with ISPS-informed guarding.",
    body: [
      "We staff gatehouses at container terminals, cruise berths, and ferry terminals from Halifax to Sydney, from Digby to Yarmouth. Every officer is trained in Transport Canada's Marine Transportation Security Regulations and the International Ship and Port Facility Security (ISPS) Code.",
      "From badge verification and TDG documentation checks at gate-in, to escorted crew changes, provisioning, and bunker deliveries, our officers integrate with your facility security officer and port-state control obligations.",
      "For cruise-season peaks in Halifax and Sydney, we scale to hundreds of officer-hours per port day, in step with your homeporting or transit-call schedules.",
    ],
    deliverables: [
      "Terminal gate control",
      "ISPS-informed guarding",
      "Escorted provisioning & crew changes",
      "Cruise-season surge capacity",
      "Facility security officer support",
    ],
  },
  {
    slug: "industrial-facility-security",
    number: "11",
    name: "Industrial Facility Security",
    shortName: "Industrial",
    tagline: "Refineries, wind farms, paper mills, and utility yards.",
    keywords: [
      "industrial security Nova Scotia",
      "utility security Halifax",
      "wind farm security Nova Scotia",
      "paper mill security Pictou",
      "refinery security Dartmouth",
      "power plant security Nova Scotia",
    ],
    hero: "https://images.unsplash.com/photo-1660748255246-80f24d317070?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwbWluaW5nJTIwbmlnaHR8ZW58MHx8fHwxNzg1MTA1NjcyfDA&ixlib=rb-4.1.0&q=85",
    intro: "Industrial operations from the Strait of Canso to the Windsor causeway rely on us to protect fuel farms, wind assets, generation plants, and heavy industry.",
    body: [
      "Our industrial teams work inside your MOC and JHA processes, understand lockout/tagout, and can hold radio discipline on shared industrial channels.",
      "Wind-farm turbine base guarding across Amherst, Antigonish, and Guysborough counties. Fuel-farm and refinery perimeter protection in the Dartmouth/Burnside industrial complex. Paper-mill and pulp operation coverage in Pictou County.",
      "We interface with your emergency response team, your environmental officer, and your corporate security director.",
    ],
    deliverables: [
      "MOC & JHA-compliant guarding",
      "Wind-turbine base protection",
      "Fuel-farm & refinery perimeter",
      "Emergency response coordination",
      "Environmental incident awareness",
    ],
  },
  {
    slug: "cannabis-facility-security",
    number: "12",
    name: "Cannabis Facility Security",
    shortName: "Cannabis",
    tagline: "Health Canada-informed guarding for licensed producers and retail.",
    keywords: [
      "cannabis security Nova Scotia",
      "licensed producer security Halifax",
      "NSLC cannabis retail security",
      "grow op security Nova Scotia",
      "cannabis vault security Halifax",
    ],
    hero: "https://images.unsplash.com/photo-1566245024852-04fbf7842ce9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxzZWN1cml0eSUyMGd1YXJkJTIwcGF0cm9sJTIwZm9nfGVufDB8fHx8MTc4NTEwNTY3Mnww&ixlib=rb-4.1.0&q=85",
    intro: "Licensed producers, processors, and NSLC-adjacent retail operations across Nova Scotia rely on our compliant guarding program.",
    body: [
      "We work within Health Canada's Cannabis Regulations physical security requirements, including intrusion detection integration, restricted-access-zone monitoring, and visual recording device oversight.",
      "Our officers are cleared, trained, and understand the difference between a delivery ledger and an audit request.",
      "Retail assignments include NSLC-associated locations and private specialty retailers, delivered with hospitality-grade professionalism.",
    ],
    deliverables: [
      "Health Canada-informed protocols",
      "Restricted-zone monitoring",
      "Vault & inventory oversight",
      "Retail hospitality-grade guarding",
      "Audit-ready reporting",
    ],
  },
  {
    slug: "healthcare-security",
    number: "13",
    name: "Healthcare Security",
    shortName: "Healthcare",
    tagline: "Trained officers for hospitals, clinics, and long-term care.",
    keywords: [
      "hospital security Halifax",
      "healthcare security Nova Scotia",
      "clinic security guards Halifax",
      "long-term care security Nova Scotia",
      "mental health facility security Halifax",
    ],
    hero: "https://images.unsplash.com/photo-1566245024852-04fbf7842ce9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxzZWN1cml0eSUyMGd1YXJkJTIwcGF0cm9sJTIwZm9nfGVufDB8fHx8MTc4NTEwNTY3Mnww&ixlib=rb-4.1.0&q=85",
    intro: "Nova Scotia Health facilities, private clinics, and long-term care homes depend on officers who understand Code White, Code Silver, and dignity-first de-escalation.",
    body: [
      "Our healthcare officers receive additional training in trauma-informed response, non-violent crisis intervention, and Nova Scotia Health incident protocols.",
      "We support Code White responses, ED patient standby, wander-prevention in long-term care, and clinic front-desk coverage.",
      "Every officer understands PHIA and PIPEDA privacy obligations.",
    ],
    deliverables: [
      "Code White & Code Silver response",
      "ED patient standby",
      "Wander prevention in LTC",
      "Trauma-informed de-escalation",
      "PHIA-aware documentation",
    ],
  },
  {
    slug: "hotel-hospitality-security",
    number: "14",
    name: "Hotel & Hospitality Security",
    shortName: "Hotel",
    tagline: "Guest-first officers for hotels, resorts, and short-term rentals.",
    keywords: [
      "hotel security Halifax",
      "resort security Nova Scotia",
      "hospitality security guards Halifax",
      "conference hotel security Halifax",
      "boutique hotel security Lunenburg",
    ],
    hero: "https://images.unsplash.com/photo-1706555693525-fb90cbe18826?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxub3ZhJTIwc2NvdGlhJTIwY29hc3R8ZW58MHx8fHwxNzg1MTA1Njc4fDA&ixlib=rb-4.1.0&q=85",
    intro: "Nova Scotia's tourism economy — Halifax convention hotels, Cabot Trail resorts, Digby oceanfront properties — deserves security that never intrudes on the guest experience.",
    body: [
      "Our hospitality officers are trained in hotel-specific de-escalation, discreet response to noise complaints, safe intoxicated-guest handling, and Duty of Care obligations.",
      "For conference and wedding weekends, we scale to include roving officers, function-room monitors, and after-hours lobby coverage.",
      "We coordinate directly with hotel management, food and beverage supervisors, and event planners.",
    ],
    deliverables: [
      "Roving guest-floor patrols",
      "Discreet noise-complaint response",
      "Conference & wedding coverage",
      "Intoxicated-guest handling",
      "Front-of-house support",
    ],
  },
  {
    slug: "fire-watch",
    number: "15",
    name: "Fire Watch Services",
    shortName: "Fire Watch",
    tagline: "Certified fire watch officers for impaired-system, hot-work, and construction-phase compliance.",
    keywords: [
      "fire watch services Nova Scotia",
      "fire watch security Halifax",
      "fire watch guards Dartmouth",
      "fire watch Cape Breton",
      "hot work fire watch Nova Scotia",
      "impaired sprinkler system fire watch Halifax",
      "fire alarm impairment watch Nova Scotia",
      "construction fire watch Halifax",
      "welding fire watch Nova Scotia",
      "NFPA 25 fire watch compliance Halifax",
      "24/7 fire watch guards Nova Scotia",
      "certified fire watch officer Halifax",
      "fire watch Truro",
      "fire watch Sydney NS",
      "hospital fire watch Nova Scotia",
    ],
    hero: "https://images.unsplash.com/photo-1660748255246-80f24d317070?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwbWluaW5nJTIwbmlnaHR8ZW58MHx8fHwxNzg1MTA1NjcyfDA&ixlib=rb-4.1.0&q=85",
    intro: "When a sprinkler is impaired, a fire alarm panel is offline, or a hot-work permit is issued, Nova Scotia's Fire Safety Act and the National Fire Code of Canada require a dedicated fire watch. We deploy certified, log-carrying officers within hours — on hospital wings, hotel floors, industrial plants, and construction sites — the moment your protection system is compromised.",
    body: [
      "A fire watch is not a security posting — it is a life-safety obligation. Our fire watch officers are trained in NFC-2020 and NFPA 25/72 impairment procedures, know how to distinguish smoke from process vapour, are drilled on evacuation and alarm-pull protocols, and carry the exact log book that a Halifax Regional Fire Inspector or your insurer's loss-control engineer will ask to see.",
      "We are dispatched most often for four scenarios: (1) sprinkler system impairment during retrofit or repair, (2) fire alarm panel or detection loop offline for maintenance, (3) hot-work operations — welding, cutting, grinding, roofing torches — under a permit that requires a dedicated watcher for the burn window plus 60 minutes, and (4) construction-phase fire watch on wood-frame highrises where the standpipe or sprinkler system is not yet functional.",
      "Every officer we assign to a fire watch post in Nova Scotia carries a provincial security licence, a current Standard First Aid + CPR-C certificate, an active WHMIS certification, hot-work-permit awareness, and site-specific orientation completed before the first shift. Our supervisors have working relationships with Halifax Regional Fire & Emergency, Cape Breton Regional Fire, and the provincial Office of the Fire Marshal — so your inspector never meets a stranger at the door.",
      "Fire watch documentation is where most sites fail their inspection. Our log book captures a timestamped patrol every 15, 30, or 60 minutes (per your impairment plan), each panel and pull-station checked, ambient conditions, any anomalies, radio contacts with the fire safety coordinator, and evacuation drills conducted. Logs are handed to your facility manager at every shift change and archived digitally for insurance and AHJ disclosure.",
      "For hot-work watchers specifically, we deploy officers with dedicated ABC extinguishers, fire blankets, and thermal-scan awareness. The watcher stays with the burn area for the full permit window plus the mandatory cool-down period — no exceptions, no early departures, even on Friday afternoons.",
      "We scale from a single overnight shift to full 24/7 multi-post coverage for large impairments. For hospital wings — where zoning, patient-mobility limits, and code-response protocols matter — we coordinate directly with Nova Scotia Health facility engineering and infection-control officers. For hotels, we work around banquet schedules and quiet-hours obligations. For industrial and mining sites, we integrate with your JHSC and MOC processes.",
      "Because fire watch is time-sensitive, we hold reserve capacity in Halifax, Dartmouth, Sydney, Truro, and New Glasgow specifically to respond to same-day dispatches. When your sprinkler contractor calls you at 2 p.m. to say the system will be down at 6 p.m., our supervisor can have a licensed, certified watcher on your site within hours — with paperwork the AHJ will accept.",
    ],
    deliverables: [
      "Certified fire watch officers, NFC-2020 / NFPA-informed",
      "Hot-work permit watchers with extinguishers & fire blankets",
      "15/30/60-minute timestamped patrol logs",
      "Impairment plan coordination with your fire safety coordinator",
      "Direct interface with AHJ and insurance loss-control engineers",
      "Same-day dispatch reserve across HRM, CBRM, and Central NS",
      "24/7 multi-post coverage for large impairments",
      "Archived digital log delivery for audit & disclosure",
    ],
  },
];

export const getService = (slug) => services.find((s) => s.slug === slug);
